// NotificationSound — single source of truth for the staff notification sound.
//
// Design (post-bugfix v2)
// ───────────────────────
// 1. Detection by ID SET, not by timestamp. The polling layer passes every
//    notification ID it saw on the latest fetch to `notifyMaybePlay`. The
//    first time we see a user, we snapshot that set as the "baseline" and
//    DO NOT play. On every later tick, if any ID is NOT in the baseline
//    set, we play exactly once and merge the new IDs into the baseline.
//    This is immune to server/browser clock skew.
// 2. The polling interval is owned by AppLayout (10 s + visibilitychange).
//    No separate heavy loop lives in this file.
// 3. First user gesture (click / tap / keydown) unlocks audio. If a
//    notification arrives while audio is still locked, we remember that a
//    play is pending and expose `hasPendingPlay()` so the bell panel can
//    show a "Tap here to enable sound" hint; the hint itself unlocks audio
//    and triggers the pending play.
// 4. If a sound is already playing we drop the next request (no stacking).
// 5. Autoplay / decoding failures fail silently.
// 6. Client / Client Manager roles never hear anything — the tab title
//    unread-count prefix is still kept in sync by `updateTabTitle`.

const AUDIO_URL = "/sounds/notification.mp3";
const CLIENT_ROLES = new Set(["Client", "Client Manager"]);
const BASE_TITLE_KEY = "__notif_base_title__";

let _audio = null;         // HTMLAudioElement — preloaded MP3
let _unlocked = false;     // true after the first user gesture
let _unlockListenersAttached = false;
let _isPlaying = false;    // guard against stacking a burst
let _seenIds = null;       // Set<string> of all notification IDs we've seen
let _audioCtx = null;      // lazy AudioContext for the soft chime
let _pendingPrefs = null;  // { sound_choice, volume } captured for post-unlock play
let _playWatchdog = null;  // resets _isPlaying if a play never ends
const _hintListeners = new Set();

function emitHintChange() {
  for (const fn of _hintListeners) {
    try { fn(hasPendingPlay()); } catch { /* silent */ }
  }
}

/** Subscribe to pending-play state changes. Returns an unsubscribe fn. */
export function onPendingPlayChange(fn) {
  _hintListeners.add(fn);
  return () => _hintListeners.delete(fn);
}

/** True when the previous notification could not play because audio was
 *  still locked — the UI should surface a hint. */
export function hasPendingPlay() {
  return !!_pendingPrefs;
}

/** Lazy — construct and preload the <audio> element exactly once. */
function ensureAudio() {
  if (_audio) return _audio;
  try {
    _audio = new Audio(AUDIO_URL);
    _audio.preload = "auto";
    const clear = () => { _isPlaying = false; };
    _audio.addEventListener("ended",    clear);
    _audio.addEventListener("error",    clear);
    _audio.addEventListener("pause",    clear);
    _audio.load();
  } catch { /* silent */ }
  return _audio;
}

/** Unlock audio on a user gesture. If a notification is waiting, play it
 *  right away: that real play is itself the unlock. Only when nothing is
 *  waiting do we play the file muted to warm it up. (Doing both at once made
 *  the muted warm-up's pause() cut off the real sound, so notifications that
 *  arrived before the first click were never heard.) */
function unlockAndFlush() {
  if (_unlocked) return;
  _unlocked = true;
  if (_pendingPrefs) {
    const prefs = _pendingPrefs;
    _pendingPrefs = null;
    emitHintChange();
    playNow(prefs);
    return;
  }
  const a = ensureAudio();
  if (!a) return;
  try {
    const old = a.volume;
    a.volume = 0;
    const p = a.play();
    const restore = () => {
      // A real notification may have started meanwhile; leave it alone.
      if (_isPlaying) return;
      a.pause(); a.currentTime = 0; a.volume = old;
    };
    if (p && typeof p.then === "function") {
      p.then(restore).catch(() => { a.volume = old; });
    } else {
      restore();
    }
  } catch { /* silent */ }
}

/** First user gesture unlocks audio. Called from AppLayout mount AND auto-
 *  invoked on module import so the listener is attached before the user
 *  reaches the login submit button. Idempotent. */
export function installAudioUnlock() {
  if (_unlockListenersAttached) return;
  _unlockListenersAttached = true;
  document.addEventListener("click",      unlockAndFlush, true);
  document.addEventListener("keydown",    unlockAndFlush, true);
  document.addEventListener("touchstart", unlockAndFlush, true);
  ensureAudio();
}

/** Called right after a successful login API response: the submit click is
 *  the unlocking gesture. Safe to call multiple times. */
export function markAudioUnlocked() {
  unlockAndFlush();
}

// Auto-install on module import so the listener is live before the first
// click ever happens. In practice this means the login submit click counts
// as the audio-unlock gesture.
if (typeof document !== "undefined") {
  try { installAudioUnlock(); } catch { /* silent */ }
}

/** Reset the baseline (used on logout or when the user switches). */
export function resetNotificationSound() {
  _seenIds = null;
  _isPlaying = false;
  _pendingPrefs = null;
  if (_audio) {
    try { _audio.pause(); _audio.currentTime = 0; } catch { /* silent */ }
  }
  emitHintChange();
}

/** Keep the tab title's "(N)" prefix in sync with the unread count. */
export function updateTabTitle(unreadCount) {
  try {
    if (!window[BASE_TITLE_KEY]) {
      window[BASE_TITLE_KEY] = (document.title || "").replace(/^\(\d+\)\s+/, "");
    }
    const base = window[BASE_TITLE_KEY] || "SHINE";
    const n = Math.max(0, Number(unreadCount) || 0);
    document.title = n > 0 ? `(${n}) ${base}` : base;
  } catch { /* silent */ }
}

/** Produce a ~1 second soft chime via Web Audio (no asset). */
function playSoftChime(volume = 0.7) {
  return new Promise((resolve) => {
    try {
      if (!_audioCtx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return resolve();
        _audioCtx = new Ctx();
      }
      if (_audioCtx.state === "suspended") {
        _audioCtx.resume().catch(() => {});
      }
      const now = _audioCtx.currentTime;
      const osc = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(659.25, now + 0.6);
      const peak = Math.max(0, Math.min(1, Number(volume) || 0)) * 0.3;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(peak, now + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.95);
      osc.connect(gain).connect(_audioCtx.destination);
      osc.start(now);
      osc.stop(now + 1.0);
      osc.onended = () => resolve();
    } catch { resolve(); }
  });
}

/**
 * Called by the AppLayout polling loop after every /notifications fetch.
 *
 * @param {object} opts
 * @param {Array<{id:string}>} opts.notifications — ALL rows returned by the
 *   current fetch (read AND unread). Only `id` is required; read/unread is
 *   not consulted — a notification is "new" iff its id was never seen.
 * @param {object} opts.user — current user; `role` and `notification_prefs`
 *   are consulted. Client / Client Manager → no sound ever.
 */
export function notifyMaybePlay({ notifications, user }) {
  const ids = (notifications || [])
    .map((n) => n && n.id)
    .filter((x) => typeof x === "string" && x.length > 0);

  // First poll for this login: snapshot every id as "already seen", no play.
  if (_seenIds === null) {
    _seenIds = new Set(ids);
    return;
  }

  // Build the "new" subset — ids present now that were NOT in the baseline.
  const unseen = [];
  for (const id of ids) {
    if (!_seenIds.has(id)) unseen.push(id);
  }

  // Always keep the baseline up to date so this burst isn't replayed on the
  // next poll — even if we end up skipping playback due to role/prefs.
  for (const id of unseen) _seenIds.add(id);
  if (unseen.length === 0) return;

  // Role gate — client portal roles never hear a sound.
  if (!user || CLIENT_ROLES.has(user.role)) return;

  const prefs = user.notification_prefs || {};
  if (prefs.sound_on === false) return;

  const spec = {
    sound_choice: prefs.sound_choice || "pikachu",
    volume: typeof prefs.volume === "number" ? prefs.volume : 0.7,
  };

  if (!_unlocked) {
    // Remember the latest spec; the next user gesture will flush it.
    _pendingPrefs = spec;
    emitHintChange();
    return;
  }

  if (_isPlaying) return;  // no stacking
  playNow(spec);
}

/** Shared play routine used by both the poller and the Test button. */
function playNow({ sound_choice = "pikachu", volume = 0.7 } = {}) {
  if (_isPlaying) return;
  _isPlaying = true;
  const v = Math.max(0, Math.min(1, Number(volume) || 0));

  if (sound_choice === "chime") {
    playSoftChime(v).finally(() => { _isPlaying = false; });
    return;
  }

  const a = ensureAudio();
  if (!a) { _isPlaying = false; return; }
  // Safety net: never let a stalled play() keep every later sound muted.
  clearTimeout(_playWatchdog);
  _playWatchdog = setTimeout(() => { _isPlaying = false; }, 8000);
  try {
    a.currentTime = 0;
    a.volume = v;
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.catch(() => { _isPlaying = false; });  // autoplay denied → silent
    }
  } catch {
    _isPlaying = false;
  }
}

/** Explicit test from Settings — bypasses the "seen" check. */
export function testPlayNotificationSound({ user, sound_choice, volume }) {
  if (!user || CLIENT_ROLES.has(user.role)) return;
  playNow({ sound_choice, volume });
}

/** Called when the UI's "Tap here to enable sound" hint is clicked. The
 *  click itself unlocks audio via the global listener; this just flushes
 *  the pending spec explicitly in case the global listener raced. */
export function flushPendingPlay() {
  if (!_pendingPrefs) return;
  const spec = _pendingPrefs;
  _pendingPrefs = null;
  emitHintChange();
  _unlocked = true;
  playNow(spec);
}
